<template>
  <transition
    name="modal-transition"
    @after-leave="afterLeave"
  >
    <div
      class="modal"
      :class="classObject"
      tabindex="-1"
      @click="closeHandler"
    >
      <section
        class="modal__wrapper"
        @click.stop
      >
        <action
          v-if="!noClose"
          class="modal__close"
          color="primary"
          square
          @click="closeHandler"
        >
          <span class="sr-only">{{ $t('close') }}</span>
          <icon type="close" />
        </action>

        <div class="modal__box">
          <header
            v-if="$slots.header"
            class="modal__header"
          >
            <slot name="header" />
          </header>

          <header
            v-if="title"
            class="modal__header modal__header--padding"
          >
            <headline level="2">
              {{ title }}
            </headline>
          </header>

          <div class="modal__content">
            <slot />
          </div>

          <footer
            v-if="$slots.footer"
            class="modal__footer"
          >
            <slot name="footer" />
          </footer>
        </div>
      </section>
    </div>
  </transition>
</template>

<script>
import { tabbable } from 'tabbable'

import getScrollbarWidth from '../functions/getScrollbarWidth'

import action from './action.vue'
import headline from './headline.vue'
import icon from './icon.vue'

export default {
  name: 'CModal',

  components: {
    action,
    headline,
    icon
  },

  props: {
    centerContent: Boolean,
    noClose: Boolean,
    size: {
      type: String,
      default: undefined
    },
    title: {
      type: String,
      default: undefined
    }
  },

  emits: ['close'],

  data () {
    return {
      previousActiveElement: null,
      tabbableElements: null
    }
  },

  computed: {
    classObject () {
      return {
        'modal--small': this.size === 'small',
        'modal--medium': this.size === 'medium',
        'modal--large': this.size === 'large',
        'modal--center-content': this.centerContent
      }
    }
  },

  mounted () {
    // Replace scrollbar with a padding, so the user can only scroll the modal
    document.body.style.paddingRight = getScrollbarWidth() + 'px'
    document.body.style.overflowY = 'hidden'

    // Save previously focused element
    this.previousActiveElement = document.activeElement
    this.$el.focus()

    // Keep keyboard tabbing within modal
    this.tabbableElements = tabbable(this.$el)

    if (this.tabbableElements.length > 0) {
      this.tabbableElements[0].addEventListener('keydown', event => {
        if (event.keyCode === 9 && event.shiftKey) {
          event.preventDefault()
          this.tabbableElements[this.tabbableElements.length - 1].focus()
        }
      })

      this.tabbableElements[this.tabbableElements.length - 1].addEventListener('keydown', event => {
        if (event.keyCode === 9 && !event.shiftKey) {
          event.preventDefault()
          this.tabbableElements[0].focus()
        }
      })
    }

    document.addEventListener('keydown', this.escapeKeyHandler)
  },

  destroyed () {
    // Move the focus back to the saved element
    this.previousActiveElement.focus()

    document.removeEventListener('keydown', this.escapeKeyHandler)
  },

  methods: {
    afterLeave () {
      // Re-enable scrollbar after transition finished
      document.body.style.overflowY = ''
      document.body.style.paddingRight = ''
    },
    closeHandler () {
      if (this.noClose) {
        return
      }

      this.$emit('close')
    },
    escapeKeyHandler (event) {
      if (this.noClose) {
        return
      }

      if (event.keyCode === 27) {
        this.$emit('close')
      }
    }
  }
}
</script>

<i18n locale="en">
{
  "close": "Close"
}
</i18n>

<i18n locale="de">
{
  "close": "Schließen"
}
</i18n>

<style lang="scss">
@import "../css/variables";

$breakpoint-header-fixed: 50rem;

.modal-transition-enter-active,
.modal-transition-leave-active {

  @media (prefers-reduced-motion: no-preference) {
    transition: opacity 0.2s;

    .modal__wrapper {
      transition: transform 0.2s;
    }
  }
}

.modal-transition-enter,
.modal-transition-leave-active {
  opacity: 0;

  .modal__wrapper {
    transform: scale(0.9);
  }
}

.modal {
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: $z-index-modal;

  background-color: rgb(0 0 0 / 0.7);
}

.modal--center-content {
  text-align: center;
}

.modal__wrapper {
  position: relative;
  max-width: calc(100vw - 2em);
  max-height: calc(100vh - 2em);

  .modal--small & {
    width: 20rem;
  }

  .modal--medium & {
    width: 30rem;
  }

  .modal--large & {
    width: 50rem;
  }
}

.modal__close {
  position: absolute;
  top: -1em;
  right: -1em;
  z-index: 10;
}

.modal__box {
  display: flex;
  flex-direction: column;
  max-height: inherit;

  background-color: #fff;
  overflow-y: auto;
  overflow-x: hidden;
}

.modal__header--padding {
  padding: $gutter-sm $gutter-sm 0;

  @media (min-width: $breakpoint-md) {
    padding: $gutter-md $gutter-md 0;
  }
}

.modal__content {
  padding: $gutter-sm;

  > :first-child {
    margin-top: 0;
  }

  > :last-child {
    margin-bottom: 0;
  }

  @media (min-width: $breakpoint-md) {
    padding: $gutter-md;
  }
}

.modal__footer {
  padding: 0 $gutter-sm $gutter-sm;

  text-align: center;

  > :first-child {
    margin-top: 0;
  }

  > :last-child {
    margin-bottom: 0;
  }

  @media (min-width: $breakpoint-md) {
    padding: 0 $gutter-md $gutter-md;
  }
}
</style>
